/**
 * Generator - Simplified TypeScript generation for NativeBrickModule.ts only
 * Only generates the TurboModule specification file needed for React Native Codegen
 */

import crypto from "crypto";
import fs from "fs-extra";
import path from "path";
import pc from "picocolors";
import { BrickCodegenError } from "./errors";
import type { ComponentConfig } from "./main-index";
import type { ModuleEvent, ModuleMethod, ParsedModule } from "./parser";
import type { PlatformRegistry } from "./platforms";

export interface GenerationResult {
  generatedFiles: string[];
  warnings: string[];
}

export class Generator {
  private config: ComponentConfig;
  private platformRegistry?: PlatformRegistry;

  constructor(config: ComponentConfig, platformRegistry?: PlatformRegistry) {
    this.config = config;
    this.platformRegistry = platformRegistry;
  }

  /**
   * Main generation method - Generates .brick folder structure with package.json and NativeBrickModule.ts
   */
  async generateTypeScriptFiles(modules: ParsedModule[]): Promise<string[]> {
    try {
      const brickDir = path.join(
        this.config.projectRoot,
        this.config.outputTypescript
      );
      const srcDir = path.join(brickDir, "src");
      const specDir = path.join(srcDir, "spec");
      const generatedFiles: string[] = [];

      await fs.ensureDir(specDir);

      // Generate package.json for .brick folder
      const packageJsonFile = await this.generatePackageJson(brickDir);
      generatedFiles.push(packageJsonFile);

      // Generate TurboModule spec file in .brick/src/spec/
      const turboSpecFile = await this.generateTurboModuleSpec(
        modules,
        specDir
      );
      generatedFiles.push(turboSpecFile);

      return generatedFiles;
    } catch (error) {
      if (error instanceof BrickCodegenError) {
        throw error;
      }
      throw new BrickCodegenError(
        "Failed to generate TypeScript files",
        {},
        error instanceof Error ? error : undefined
      );
    }
  }

  /**
   * Generates package.json for .brick folder
   */
  private async generatePackageJson(brickDir: string): Promise<string> {
    // 현재 프로젝트의 package.json에서 name을 읽어와서 hash 생성
    const projectPackageJsonPath = path.join(
      this.config.projectRoot,
      "package.json"
    );
    let projectName = Math.random().toString(36).substring(2, 15);

    try {
      const projectPackageJson = await fs.readFile(
        projectPackageJsonPath,
        "utf-8"
      );
      const projectPackage = JSON.parse(projectPackageJson);
      projectName =
        projectPackage.name || Math.random().toString(36).substring(2, 15);
    } catch (error) {
      console.error(error instanceof Error ? error.message : String(error));
    }

    // 프로젝트 이름을 hash하여 고유한 suffix 생성
    const nameHash = crypto
      .createHash("md5")
      .update(projectName)
      .digest("hex")
      .substring(0, 8);

    const packageJson = {
      name: `brick-${nameHash}`,
      codegenConfig: {
        name: "BrickModuleSpec",
        type: "modules",
        jsSrcsDir: "src/spec",
      },
    };

    const outputPath = path.join(brickDir, "package.json");

    await fs.writeFile(outputPath, JSON.stringify(packageJson, null, 2));
    this.logGenerated(outputPath);

    return outputPath;
  }

  /**
   * Generates TurboModule specification for React Native Codegen
   * This is the only file generated by brick-codegen
   */
  private async generateTurboModuleSpec(
    modules: ParsedModule[],
    specDir: string
  ): Promise<string> {
    const methods = this.getAllMethods(modules);
    const constants = this.getAllConstants(modules);
    const events = this.getAllEvents(modules);

    // Collect interface definitions from all modules
    const interfaceDefinitions: string[] = [];
    const seenInterfaces = new Set<string>();

    for (const module of modules) {
      // Add interfaces from parsed module with module prefix
      for (const interfaceDef of module.interfaceDefinitions) {
        if (
          !interfaceDef.name.includes("ModuleSpec") &&
          !interfaceDef.name.includes("Events")
        ) {
          const prefixedName = `${module.moduleName}_${interfaceDef.name}`;

          if (!seenInterfaces.has(prefixedName)) {
            seenInterfaces.add(prefixedName);

            // Generate interface code with prefixed property types
            const properties = interfaceDef.properties
              .map((prop) => {
                const optionalMark = prop.optional ? "?" : "";
                // Add module prefix to type references if they're custom types
                let propType = prop.type;
                // Check if it's a reference to another interface
                for (const otherInterface of module.interfaceDefinitions) {
                  if (
                    propType.includes(otherInterface.name) &&
                    !otherInterface.name.includes("ModuleSpec") &&
                    !otherInterface.name.includes("Events")
                  ) {
                    propType = propType.replace(
                      new RegExp(`\\b${otherInterface.name}\\b`, "g"),
                      `${module.moduleName}_${otherInterface.name}`
                    );
                  }
                }
                return `  ${prop.name}${optionalMark}: ${propType};`;
              })
              .join("\n");

            interfaceDefinitions.push(
              `export interface ${prefixedName} {\n${properties}\n}`
            );
          }
        }
      }
    }

    // Create a mapping of original type names to prefixed names for all modules
    const typeMappings = new Map<string, string>();
    for (const module of modules) {
      for (const interfaceDef of module.interfaceDefinitions) {
        if (
          !interfaceDef.name.includes("ModuleSpec") &&
          !interfaceDef.name.includes("Events")
        ) {
          typeMappings.set(
            interfaceDef.name,
            `${module.moduleName}_${interfaceDef.name}`
          );
        }
      }
    }

    const methodDeclarations = methods
      .map((method) => {
        const methodKey = `${method.moduleName}_${method.name}`;
        let parameterPart = method.signature.substring(method.name.length);

        // Replace type references with prefixed versions
        typeMappings.forEach((prefixedName, originalName) => {
          // Match the type name when it appears as a parameter type or return type
          const regex = new RegExp(`\\b${originalName}\\b`, "g");
          parameterPart = parameterPart.replace(regex, prefixedName);
        });

        // Normalize special generic object aliases for RN Codegen Surface
        // Map AnyObject (used in module specs) to {} in NativeBrickModule.ts
        parameterPart = parameterPart.replace(/\bAnyObject\b/g, "{}");
        // Map Any (any value type) to {} in NativeBrickModule.ts
        parameterPart = parameterPart.replace(/\bAny\b/g, "{}");

        // For sync methods (non-async), change return type to {} for wrapped result
        // React Native Codegen prefers {} over Object type
        if (!method.isAsync) {
          // Extract return type from signature
          // Signature format: (params): ReturnType
          // Match the last colon that's outside parentheses (return type colon)
          // We need to find ): ReturnType pattern at the end
          const returnTypeMatch = parameterPart.match(/\):\s*[^;]+$/);
          if (returnTypeMatch) {
            // Replace only the return type part after ): with {}
            parameterPart = parameterPart.replace(/\):\s*[^;]+$/, "): {}");
          }
        }

        return `  ${methodKey}${parameterPart};`;
      })
      .join("\n");

    // Generate event emitter declarations
    const eventDeclarations =
      events.length > 0
        ? `\n  // ========== EVENTS ==========\n${events
            .map((event) => {
              const eventKey = `${event.moduleName}_on${this.capitalizeFirst(
                event.name
              )}`;
              // Map payload type with prefixes and normalize Any/AnyObject
              let payloadType = event.payload || "{}";
              typeMappings.forEach((prefixedName, originalName) => {
                const regex = new RegExp(`\\b${originalName}\\b`, "g");
                payloadType = payloadType.replace(regex, prefixedName);
              });
              payloadType = payloadType.replace(/\bAnyObject\b/g, "{}");
              payloadType = payloadType.replace(/\bAny\b/g, "{}");
              return `  readonly ${eventKey}: CodegenTypes.EventEmitter<${payloadType}>;`;
            })
            .join("\n")}\n`
        : "";

    // Generate constants property
    const constantsDeclarations =
      constants.length > 0
        ? `\n  // ========== CONSTANTS ==========\n  readonly getConstants: () => {\n${constants
            .map((constant) => {
              const constantKey = `${constant.moduleName}_${constant.name}`;
              return `    ${constantKey}: ${constant.type};`;
            })
            .join("\n")}\n  };\n`
        : "";

    // Add type definitions section if any types were found
    const typeDefinitionsSection =
      interfaceDefinitions.length > 0
        ? `\n// ========== TYPE DEFINITIONS ==========\n${interfaceDefinitions.join(
            "\n\n"
          )}\n`
        : "";

    const specCode = `// This file is automatically generated by brick-codegen for React Native Codegen
// Do not edit manually - regenerate using: brick-codegen
// Note: This uses the same format as NativeBrickModule.ts for consistency

import type { TurboModule, CodegenTypes } from 'react-native';
import { TurboModuleRegistry } from 'react-native';
${typeDefinitionsSection}
/**
 * TurboModule specification for React Native Codegen
 * This file is used by React Native's official codegen to generate native bindings
 */
export interface Spec extends TurboModule {
  // ========== MODULE MANAGEMENT ==========
  /**
   * Gets list of all registered Brick modules
   */
  getRegisteredModules(): string[];
  ${constantsDeclarations}${eventDeclarations}
  // ========== GENERATED MODULE METHODS ==========
${methodDeclarations}
}

export default TurboModuleRegistry.getEnforcing<Spec>('BrickModule');
`;

    const outputPath = path.join(specDir, "NativeBrickModule.ts");

    await fs.writeFile(outputPath, specCode);
    this.logGenerated(outputPath);

    return outputPath;
  }

  private getAllEvents(
    modules: ParsedModule[]
  ): Array<ModuleEvent & { moduleName: string }> {
    const events: Array<ModuleEvent & { moduleName: string }> = [];
    for (const module of modules) {
      if (!module || !module.events || !Array.isArray(module.events)) continue;
      for (const event of module.events) {
        events.push({ ...event, moduleName: module.moduleName });
      }
    }
    return events;
  }

  private capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /**
   * Gets all methods from all modules
   */
  private getAllMethods(
    modules: ParsedModule[]
  ): Array<ModuleMethod & { moduleName: string }> {
    const methods: Array<ModuleMethod & { moduleName: string }> = [];

    for (const module of modules) {
      for (const method of module.methods) {
        methods.push({
          ...method,
          moduleName: module.moduleName,
        });
      }
    }

    return methods;
  }

  /**
   * Gets all constants from all modules
   */
  private getAllConstants(modules: ParsedModule[]): Array<{
    moduleName: string;
    name: string;
    type: string;
    readonly: boolean;
  }> {
    const constants: Array<{
      moduleName: string;
      name: string;
      type: string;
      readonly: boolean;
    }> = [];

    for (const module of modules) {
      for (const constant of module.constants) {
        constants.push({
          moduleName: module.moduleName,
          name: constant.name,
          type: constant.type,
          readonly: constant.readonly,
        });
      }
    }

    return constants;
  }

  /**
   * Generates bridge code for all platforms using DI pattern
   */
  async generateBridgeCode(modules: ParsedModule[]): Promise<{
    ios: string[];
    android: string[];
    allFiles: string[];
  }> {
    const result = {
      ios: [] as string[],
      android: [] as string[],
      allFiles: [] as string[],
    };

    if (!this.platformRegistry) {
      // Skip silently if no platform registry
      return result;
    }

    // Check selectedPlatforms from config
    const selectedPlatforms = this.config.selectedPlatforms;

    // Generate iOS bridge code only if selected (or if no platforms specified)
    if (!selectedPlatforms || selectedPlatforms.includes("ios")) {
      try {
        const iosFiles = await this.platformRegistry.generateForPlatform(
          "ios",
          modules
        );
        result.ios = iosFiles;
        result.allFiles.push(...iosFiles);
        // Individual files are logged by the platform generators
      } catch (error) {
        console.error(
          `${pc.bold(pc.cyan("[Brick]"))} ${pc.red(
            `iOS bridge generation failed: ${
              error instanceof Error ? error.message : String(error)
            }`
          )}`
        );
      }
    }

    // Generate Android bridge code only if selected (or if no platforms specified)
    if (!selectedPlatforms || selectedPlatforms.includes("android")) {
      try {
        const androidFiles = await this.platformRegistry.generateForPlatform(
          "android",
          modules
        );
        result.android = androidFiles;
        result.allFiles.push(...androidFiles);
        // Individual files are logged by the platform generators
      } catch (error) {
        console.error(
          `${pc.bold(pc.cyan("[Brick]"))} ${pc.red(
            `Android bridge generation: ${
              error instanceof Error ? error.message : String(error)
            }`
          )}`
        );
      }
    }

    return result;
  }

  /**
   * Main unified generation method - Generates all files including TypeScript specs and bridge code
   */
  async generateAll(modules: ParsedModule[]): Promise<string[]> {
    const allGeneratedFiles: string[] = [];

    // 1. Generate TypeScript files (.brick folder structure)
    const tsFiles = await this.generateTypeScriptFiles(modules);
    allGeneratedFiles.push(...tsFiles);

    // 2. Generate bridge code for all platforms
    const bridgeResult = await this.generateBridgeCode(modules);
    allGeneratedFiles.push(...bridgeResult.allFiles);

    return allGeneratedFiles;
  }

  private log(message: string): void {
    console.log(`${pc.bold(pc.green("[Brick]"))} ${message}`);
  }

  private logGenerated(filepath: string): void {
    const absolutePath = path.resolve(filepath);
    console.log(
      `${pc.bold(pc.green("[Brick]"))} Generated artifact: ${absolutePath}`
    );
  }
}
